import { useCallback } from "react";
import type { Athlete, Exercise, Variants } from "types";
import type { ParsedOneRepMaxData } from "../useOneRepMaxCsvParser";

export interface ValidationResult {
  missingAthletes: Array<{ name: string; athleteId: string }>;
  missingExercises: Array<{ exerciseName: string; columns: string[] }>;
  invalidValues: Array<{ athleteName: string; exerciseName: string; value: string }>;
  validAthleteCount: number;
  exerciseCount: number;
  canProceed: boolean;
  validData: Array<{
    athleteId: string;
    oneRepMaxes: Array<{
      exerciseId: string;
      weight: number;
      variant: Variants | null;
    }>;
  }>;
}

function useOneRepMaxValidator() {
  const validate = useCallback(
    (parsedData: ParsedOneRepMaxData, athletes: Athlete[], exercises: Exercise[]): ValidationResult => {
      const missingAthletes: Array<{ name: string; athleteId: string }> = [];
      const missingExercises: Array<{ exerciseName: string; columns: string[] }> = [];
      const invalidValues: Array<{ athleteName: string; exerciseName: string; value: string }> = [];
      const validData: ValidationResult["validData"] = [];

      // Build exercise lookup using BOTH category and name (case-insensitive)
      const exerciseMap = new Map<string, Exercise>();
      exercises.forEach((exercise) => {
        const key = `${exercise.category}|${exercise.name}`.toLowerCase();
        exerciseMap.set(key, exercise);
      });

      // Build athlete lookup
      const athleteMap = new Map<string, Athlete>();
      athletes.forEach((athlete) => {
        athleteMap.set(athlete.id, athlete);
      });

      // Track found exercises
      const foundExercises = new Set<string>();

      // Validate each row
      parsedData.rows.forEach((row) => {
        // Validate athlete
        const athlete = athleteMap.get(row.athleteId);

        if (!athlete) {
          missingAthletes.push({
            name: row.athleteName,
            athleteId: row.athleteId,
          });
          return; // Skip this row
        }

        // Validate and transform exercise values
        const oneRepMaxes: ValidationResult["validData"][0]["oneRepMaxes"] = [];

        Object.entries(row.values).forEach(([fullKey, value]) => {
          if (!value) return; // Skip empty values

          // Parse the fullKey: "Category|ExerciseName" or "Category|ExerciseName (Left/Right)"
          const [category, nameWithVariant] = fullKey.split("|");

          if (!category || !nameWithVariant) {
            // Malformed key - skip
            return;
          }

          // Extract exercise name and variant
          const leftMatch = nameWithVariant.match(/^(.+?)\s*\(Left\)$/i);
          const rightMatch = nameWithVariant.match(/^(.+?)\s*\(Right\)$/i);

          let exerciseName: string;
          let variant: Variants | null = null;

          if (leftMatch) {
            exerciseName = leftMatch[1].trim();
            variant = "Left" as Variants;
          } else if (rightMatch) {
            exerciseName = rightMatch[1].trim();
            variant = "Right" as Variants;
          } else {
            exerciseName = nameWithVariant.trim();
          }

          // Find exercise using category + name (case-insensitive)
          const lookupKey = `${category}|${exerciseName}`.toLowerCase();
          const exercise = exerciseMap.get(lookupKey);

          if (!exercise) {
            // Track missing exercise with full context (Category - Exercise Name)
            const displayName = `${category} - ${nameWithVariant}`;
            const existing = missingExercises.find((e) => e.exerciseName === displayName);
            if (existing) {
              if (!existing.columns.includes(fullKey)) {
                existing.columns.push(fullKey);
              }
            } else {
              missingExercises.push({
                exerciseName: displayName,
                columns: [fullKey],
              });
            }
            return;
          }

          foundExercises.add(exercise.id);

          // Parse value
          const numericValue = parseFloat(value);

          // Skip empty, 0, or invalid values - these will be silently ignored
          if (isNaN(numericValue) || numericValue <= 0) {
            return;
          }

          // Add to valid data
          oneRepMaxes.push({
            exerciseId: exercise.id,
            weight: numericValue,
            variant,
          });
        });

        // Add athlete data if they have valid 1RM values
        if (oneRepMaxes.length > 0) {
          validData.push({
            athleteId: athlete.id,
            oneRepMaxes,
          });
        }
      });

      // Remove duplicate missing athletes
      const uniqueMissingAthletes = Array.from(new Map(missingAthletes.map((a) => [a.athleteId, a])).values());

      // Calculate stats
      const validAthleteCount = validData.length;
      const exerciseCount = foundExercises.size;

      // Can proceed if no missing exercises and no invalid values
      const canProceed = missingExercises.length === 0 && invalidValues.length === 0;

      return {
        missingAthletes: uniqueMissingAthletes,
        missingExercises,
        invalidValues,
        validAthleteCount,
        exerciseCount,
        canProceed,
        validData,
      };
    },
    []
  );

  return { validate };
}

export default useOneRepMaxValidator;
